export const idlFactory = ({ IDL }) => {
  const ConnectionInfo = IDL.Record({
    'service_name' : IDL.Opt(IDL.Text),
    'port' : IDL.Opt(IDL.Nat16),
    'connection_type' : IDL.Text,
    'target' : IDL.Text,
    'confidence' : IDL.Float32,
  });
  const ConfigAnalysisResult = IDL.Record({
    'metadata' : IDL.Record({
      'working_directory' : IDL.Opt(IDL.Text),
      'primary_service' : IDL.Opt(IDL.Text),
      'config_type' : IDL.Text,
    }),
    'connections' : IDL.Vec(ConnectionInfo),
  });
  const ChatHistoryMessage = IDL.Record({
    'id' : IDL.Text,
    'content' : IDL.Text,
    'role' : IDL.Text,
    'timestamp' : IDL.Nat64,
  });
  const CreateChatHistoryRequest = IDL.Record({
    'title' : IDL.Opt(IDL.Text),
    'messages' : IDL.Vec(ChatHistoryMessage),
  });
  const ChatHistoryEntry = IDL.Record({
    'id' : IDL.Text,
    'user_principal' : IDL.Principal,
    'title' : IDL.Text,
    'updated_at' : IDL.Nat64,
    'messages' : IDL.Vec(ChatHistoryMessage),
    'preview' : IDL.Text,
    'created_at' : IDL.Nat64,
  });
  const ClaudeStatus = IDL.Record({
    'last_error' : IDL.Opt(IDL.Text),
    'model' : IDL.Text,
    'requests_today' : IDL.Nat32,
    'enabled' : IDL.Bool,
    'configured' : IDL.Bool,
  });
  const MessageRole = IDL.Variant({
    'System' : IDL.Null,
    'User' : IDL.Null,
    'Assistant' : IDL.Null,
  });
  const Message = IDL.Record({
    'id' : IDL.Text,
    'content' : IDL.Text,
    'role' : MessageRole,
    'timestamp' : IDL.Nat64,
  });
  const Conversation = IDL.Record({
    'id' : IDL.Text,
    'updated_at' : IDL.Nat64,
    'context' : IDL.Text,
    'messages' : IDL.Vec(Message),
    'created_at' : IDL.Nat64,
    'created_by' : IDL.Principal,
  });
  const ChatHistoryListResponse = IDL.Record({
    'entries' : IDL.Vec(ChatHistoryEntry),
    'total_count' : IDL.Nat64,
  });
  const AIResponse = IDL.Record({
    'suggestions' : IDL.Vec(IDL.Text),
    'message' : IDL.Text,
    'timestamp' : IDL.Nat64,
    'confidence' : IDL.Float32,
  });
  const ClaudeError = IDL.Variant({
    'NetworkError' : IDL.Record({ 'message' : IDL.Text }),
    'Disabled' : IDL.Null,
    'ParseError' : IDL.Record({ 'message' : IDL.Text }),
    'RateLimited' : IDL.Record({ 'retry_after' : IDL.Nat64 }),
    'NotConfigured' : IDL.Null,
    'ApiError' : IDL.Record({ 'status' : IDL.Nat16, 'message' : IDL.Text }),
    'ApiKeyMissing' : IDL.Null,
  });
  const DocumentSource = IDL.Record({
    'document_name' : IDL.Text,
    'page_number' : IDL.Opt(IDL.Nat32),
    'similarity_score' : IDL.Float32,
    'chunk_preview' : IDL.Text,
  });
  const AdaBackendMode = IDL.Variant({
    'Claude' : IDL.Null,
    'LocalLLM' : IDL.Null,
    'Pattern' : IDL.Null,
  });
  const AdaResponse = IDL.Record({
    'suggestions' : IDL.Vec(IDL.Text),
    'error' : IDL.Opt(ClaudeError),
    'message' : IDL.Text,
    'timestamp' : IDL.Nat64,
    'sources' : IDL.Vec(DocumentSource),
    'confidence' : IDL.Float32,
    'backend' : AdaBackendMode,
  });
  const UpdateChatHistoryRequest = IDL.Record({
    'id' : IDL.Text,
    'title' : IDL.Opt(IDL.Text),
    'messages' : IDL.Opt(IDL.Vec(ChatHistoryMessage)),
  });
  return IDL.Service({
    'analyze_config_for_connections' : IDL.Func(
        [IDL.Text, IDL.Text, IDL.Text],
        [IDL.Variant({ 'Ok' : ConfigAnalysisResult, 'Err' : IDL.Text })],
        [],
      ),
    'clear_conversation' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Bool, 'Err' : IDL.Text })],
        [],
      ),
    'configure_claude_api' : IDL.Func(
        [IDL.Text, IDL.Bool],
        [IDL.Variant({ 'Ok' : IDL.Text, 'Err' : IDL.Text })],
        [],
      ),
    'create_chat_history_entry' : IDL.Func(
        [CreateChatHistoryRequest],
        [IDL.Variant({ 'Ok' : ChatHistoryEntry, 'Err' : IDL.Text })],
        [],
      ),
    'delete_chat_history_entry' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Bool, 'Err' : IDL.Text })],
        [],
      ),
    'disable_claude' : IDL.Func([], [IDL.Text], []),
    'get_ai_capabilities' : IDL.Func(
        [],
        [IDL.Vec(IDL.Tuple(IDL.Text, IDL.Text))],
        ['query'],
      ),
    'get_chat_history_entry' : IDL.Func(
        [IDL.Text],
        [IDL.Opt(ChatHistoryEntry)],
        ['query'],
      ),
    'get_claude_status' : IDL.Func([], [ClaudeStatus], ['query']),
    'get_conversation' : IDL.Func(
        [IDL.Text],
        [IDL.Opt(Conversation)],
        ['query'],
      ),
    'get_recent_conversations' : IDL.Func(
        [IDL.Nat64],
        [IDL.Vec(Conversation)],
        ['query'],
      ),
    'list_chat_history' : IDL.Func(
        [IDL.Nat64, IDL.Nat64],
        [ChatHistoryListResponse],
        ['query'],
      ),
    'search_chat_history_entries' : IDL.Func(
        [IDL.Text, IDL.Nat64],
        [IDL.Vec(ChatHistoryEntry)],
        ['query'],
      ),
    'send_message' : IDL.Func(
        [IDL.Text, IDL.Text],
        [IDL.Variant({ 'Ok' : AIResponse, 'Err' : IDL.Text })],
        [],
      ),
    'send_message_claude' : IDL.Func(
        [IDL.Text, IDL.Text],
        [IDL.Variant({ 'Ok' : AdaResponse, 'Err' : IDL.Text })],
        [],
      ),
    'set_claude_model' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Text, 'Err' : IDL.Text })],
        [],
      ),
    'start_conversation' : IDL.Func(
        [IDL.Text],
        [IDL.Variant({ 'Ok' : IDL.Text, 'Err' : IDL.Text })],
        [],
      ),
    'transform_claude_response' : IDL.Func(
        [
          IDL.Record({
            'context' : IDL.Vec(IDL.Nat8),
            'response' : IDL.Record({
              'status' : IDL.Nat,
              'body' : IDL.Vec(IDL.Nat8),
              'headers' : IDL.Vec(
                IDL.Record({ 'value' : IDL.Text, 'name' : IDL.Text })
              ),
            }),
          }),
        ],
        [
          IDL.Record({
            'status' : IDL.Nat,
            'body' : IDL.Vec(IDL.Nat8),
            'headers' : IDL.Vec(
              IDL.Record({ 'value' : IDL.Text, 'name' : IDL.Text })
            ),
          }),
        ],
        ['query'],
      ),
    'update_chat_history_entry' : IDL.Func(
        [UpdateChatHistoryRequest],
        [IDL.Variant({ 'Ok' : ChatHistoryEntry, 'Err' : IDL.Text })],
        [],
      ),
  });
};
export const init = ({ IDL }) => { return []; };
